import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const HEADLESS_CMS_API_URL = process.env.REACT_APP_HEADLESS_CMS_API_URL;
const perPage = 6;
const defaultCategoryKey = "all";
const languages = ["en", "es", "fr", "de", "glossary", "case_study"];
const tagsMap = {
  en: "tags_exclude=313,314,645,655,664",
  es: "tags=313",
  fr: "tags=314",
  de: "tags=645",
  glossary: "tags=655",
  case_study: "tags=664",
};

const authHeader = {
  Authorization: "Basic b2xpdmVyOmxJZWsgWUNGcCB5MEJtIDJyVFogZXY5ZiBrejJx",
};

const buildLangState = (factory) => {
  const state = {};
  languages.forEach((lang) => {
    state[lang] = factory();
  });
  return state;
};

export const fetchNews = createAsyncThunk(
  "news/fetchNews",
  async (
    {
      lang,
      page = 1,
      slug,
      categorySlug,
      perPage: perPageOverride,
      searchTerm,
      listKey,
    },
    { rejectWithValue }
  ) => {
    const params = {
      status: "publish",
      per_page: perPageOverride || perPage,
    };

    const categoryKey = listKey || categorySlug || defaultCategoryKey;

    if (slug) {
      params.slug = slug;
    } else {
      params.page = page;
    }
    if (searchTerm) {
      params.search = searchTerm;
    }

    try {
      if (!slug && categorySlug && categorySlug !== defaultCategoryKey) {
        const categoryResponse = await axios.get(
          `${HEADLESS_CMS_API_URL}/categories`,
          {
            cache: false,
            headers: authHeader,
            params: { slug: categorySlug },
          }
        );

        const categoryId = categoryResponse?.data?.[0]?.id;

        if (categoryId) {
          params.categories = categoryId;
        } else {
          return {
            lang,
            data: [],
            page,
            totalPages: 0,
            categoryKey,
            isInvalidSlug: false,
          };
        }
      }

      const response = await axios.get(
        `${HEADLESS_CMS_API_URL}/posts?${tagsMap[lang]}`,
        {
          cache: false,
          headers: authHeader,
          params,
        }
      );
      const totalPages = parseInt(response.headers["x-wp-totalpages"]);

      return {
        lang,
        data: response.data,
        page,
        totalPages,
        categoryKey,
        isInvalidSlug: slug && !response.data.length,
      };
    } catch (err) {
      return rejectWithValue(
        "Unable to fetch news information from the server."
      );
    }
  }
);

// New action to fetch ALL glossary terms for alphabetical navigation
export const fetchAllGlossaryTerms = createAsyncThunk(
  "news/fetchAllGlossaryTerms",
  async (_, { rejectWithValue }) => {
    try {
      const firstPageResponse = await axios.get(
        `${HEADLESS_CMS_API_URL}/posts?${tagsMap.glossary}`,
        {
          cache: false,
          headers: authHeader,
          params: {
            status: "publish",
            per_page: 100,
            page: 1,
          },
        }
      );

      const totalPages = parseInt(
        firstPageResponse.headers["x-wp-totalpages"] || "1",
        10
      );
      const allTerms = [...(firstPageResponse.data || [])];

      if (totalPages > 1) {
        const pageRequests = [];
        for (let page = 2; page <= totalPages; page += 1) {
          pageRequests.push(
            axios.get(`${HEADLESS_CMS_API_URL}/posts?${tagsMap.glossary}`, {
              cache: false,
              headers: authHeader,
              params: {
                status: "publish",
                per_page: 100,
                page,
              },
            })
          );
        }

        const responses = await Promise.all(pageRequests);
        responses.forEach((res) => {
          if (Array.isArray(res.data)) {
            allTerms.push(...res.data);
          }
        });
      }

      const dedupedTerms = Array.from(
        new Map(allTerms.map((term) => [term.id, term])).values()
      );

      return dedupedTerms;
    } catch (err) {
      return rejectWithValue(
        "Unable to fetch glossary terms from the server."
      );
    }
  }
);

const initialState = {
  list: buildLangState(() => ({ [defaultCategoryKey]: {} })),
  currentPage: buildLangState(() => ({ [defaultCategoryKey]: 1 })),
  totalPages: buildLangState(() => ({ [defaultCategoryKey]: 0 })),
  allGlossaryTerms: [], // Store all glossary terms for alphabetical navigation
  loading: false,
  glossaryLoading: false,
  error: null,
};

const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {
    initList: (state, action) => {
      const payload = action.payload;
      const lang = typeof payload === "string" ? payload : payload.lang;
      const category =
        typeof payload === "string"
          ? defaultCategoryKey
          : payload.category || defaultCategoryKey;

      if (!state.list[lang]) state.list[lang] = {};
      if (!state.currentPage[lang]) state.currentPage[lang] = {};
      if (!state.totalPages[lang]) state.totalPages[lang] = {};

      if (!state.list[lang][category]) state.list[lang][category] = {};
      if (!state.currentPage[lang][category])
        state.currentPage[lang][category] = 1;
      if (!state.totalPages[lang][category])
        state.totalPages[lang][category] = 0;
    },
    setCurrentPage: (state, action) => {
      const { lang, page, category = defaultCategoryKey } = action.payload;

      if (!state.currentPage[lang]) state.currentPage[lang] = {};
      state.currentPage[lang][category] = page;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchNews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNews.fulfilled, (state, action) => {
        const {
          lang,
          data,
          page,
          totalPages,
          categoryKey = defaultCategoryKey,
          isInvalidSlug,
        } = action.payload;

        state.error = isInvalidSlug;

        if (!state.list[lang]) state.list[lang] = {};
        if (!state.list[lang][categoryKey]) state.list[lang][categoryKey] = {};
        if (!state.currentPage[lang]) state.currentPage[lang] = {};
        if (!state.totalPages[lang]) state.totalPages[lang] = {};

        state.list[lang][categoryKey][page] = data;
        state.currentPage[lang][categoryKey] = page;
        state.totalPages[lang][categoryKey] = totalPages || 0;

        state.loading = false;
      })
      .addCase(fetchNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllGlossaryTerms.pending, (state) => {
        state.glossaryLoading = true;
        state.error = null;
      })
      .addCase(fetchAllGlossaryTerms.fulfilled, (state, action) => {
        state.allGlossaryTerms = action.payload;
        state.glossaryLoading = false;
      })
      .addCase(fetchAllGlossaryTerms.rejected, (state, action) => {
        state.glossaryLoading = false;
        state.error = action.payload;
      });
  },
});

export const { initList, setCurrentPage } = newsSlice.actions;

export default newsSlice.reducer;

export const getNewsBySlug = (state, payload) => {
  const { lang, slug } = payload;

  const itemsByCategory = state.news?.list?.[lang];
  if (!itemsByCategory) return null;

  const aggregated = Object.values(itemsByCategory)
    .map((pages) => Object.values(pages || {}))
    .flat(2);

  return aggregated.find((newsItem) => newsItem.slug === slug);
};

// Selector to get glossary term by slug
export const getGlossaryTermBySlug = (state, slug) => {
  const glossaryTerms = state.news?.allGlossaryTerms || [];
  return glossaryTerms.find((term) => term.slug === slug);
};
